import { type FormEvent, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { issuesApi, issueTypeConfigsApi, type IssueType, type IssueStatus, type IssuePriority } from "../../api/issues.api";
import { meApi } from "../../api/me.api";
import { useState } from "react";
import { useAttachments, AttachmentsPanel } from "../../components/shared/AttachmentsPanel";
import { useAuthStore } from "../../store/auth.store";
import { usersApi } from "../../api/users.api";

const STATUS_LABEL: Record<IssueStatus, string> = {
  OPEN: "Open",
  IN_PROGRESS: "In Progress",
  UNDER_REVIEW: "Under Review",
  RESOLVED: "Resolved",
  CLOSED: "Closed",
  WONT_FIX: "Won't Fix",
  ARCHIVED: "Archived",
};

const ALL_STATUSES: IssueStatus[] = ["OPEN", "IN_PROGRESS", "UNDER_REVIEW", "RESOLVED", "CLOSED", "WONT_FIX", "ARCHIVED"];
const ALL_PRIORITIES: IssuePriority[] = ["LOW", "MEDIUM", "HIGH", "CRITICAL"];

export function SubmitIssue() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isAdmin = useAuthStore((s) => s.user?.isPanelAdmin === true);

  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [type, setType] = useState<IssueType>("BUG");
  const [appId, setAppId] = useState("");
  const [status, setStatus] = useState<IssueStatus>("OPEN");
  const [priority, setPriority] = useState<IssuePriority>("MEDIUM");
  const [assignedToId, setAssignedToId] = useState<string>("");
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const attachments = useAttachments();

  const { data: apps } = useQuery({
    queryKey: ["me", "apps"],
    queryFn: meApi.apps,
  });

  const { data: typeConfigs } = useQuery({
    queryKey: ["issue-type-configs"],
    queryFn: issueTypeConfigsApi.list,
  });

  const { data: adminUsers } = useQuery({
    queryKey: ["users", "admins"],
    queryFn: () => usersApi.list({ panelAdminOnly: true }),
    enabled: isAdmin,
  });

  const activeTypes = typeConfigs?.filter((c) => c.isActive) ?? [];

  const submit = useMutation({
    mutationFn: () =>
      issuesApi.create({
        title,
        body: attachments.buildMarkdown(body),
        type,
        ...(appId ? { appId } : {}),
        ...(isAdmin ? {
          status,
          priority,
          assignedToId: assignedToId || null,
        } : {}),
      }),
    onSuccess: (issue) => {
      queryClient.invalidateQueries({ queryKey: ["issues"] });
      navigate(`/issues/${issue.id}`);
    },
  });

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    submit.mutate();
  }

  return (
    <>
      <div className="page-header">
        <h2>Report an Issue</h2>
      </div>

      <div className="card" style={{ maxWidth: 640 }}>
        <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <div>
            <label style={{ display: "block", marginBottom: 4, fontWeight: 500 }}>
              Issue type
            </label>
            <select
              value={type}
              onChange={(e) => setType(e.target.value as IssueType)}
              style={{ width: "100%" }}
              required
            >
              {activeTypes.map((cfg) => (
                <option key={cfg.type} value={cfg.type}>
                  {cfg.label}{cfg.description ? ` — ${cfg.description}` : ""}
                </option>
              ))}
            </select>
          </div>

          {apps && apps.length > 0 && (
            <div>
              <label style={{ display: "block", marginBottom: 4, fontWeight: 500 }}>
                Related app
              </label>
              <select
                value={appId}
                onChange={(e) => setAppId(e.target.value)}
                style={{ width: "100%" }}
                required
              >
                <option value="">— Select an app —</option>
                {apps.map((a) => (
                  <option key={a.id} value={a.id}>{a.name}</option>
                ))}
              </select>
            </div>
          )}

          <div>
            <label style={{ display: "block", marginBottom: 4, fontWeight: 500 }}>
              Title <span className="muted" style={{ fontWeight: 400 }}>(5–200 chars)</span>
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Brief summary of the issue"
              minLength={5}
              maxLength={200}
              required
              style={{ width: "100%" }}
            />
          </div>

          <div>
            <label style={{ display: "block", marginBottom: 4, fontWeight: 500 }}>
              Description
            </label>
            <textarea
              ref={textareaRef}
              value={body}
              onChange={(e) => setBody(e.target.value)}
              onPaste={attachments.handlePaste}
              placeholder="Describe the issue in detail. Include steps to reproduce if applicable."
              minLength={10}
              maxLength={10000}
              rows={7}
              required
              style={{ width: "100%", resize: "vertical" }}
            />
            <AttachmentsPanel
              files={attachments.files}
              setFiles={attachments.setFiles}
              anyUploading={attachments.anyUploading}
              processFile={attachments.processFile}
              removeFile={attachments.removeFile}
            />
          </div>

          {isAdmin && (
            <div style={{ borderTop: "1px solid var(--border-color, #333)", paddingTop: 16, display: "flex", flexDirection: "column", gap: 12 }}>
              <p style={{ margin: 0, fontWeight: 500, fontSize: "0.85rem", textTransform: "uppercase", letterSpacing: "0.05em", opacity: 0.6 }}>
                Admin Controls
              </p>

              <div>
                <label style={{ display: "block", marginBottom: 4, fontWeight: 500 }}>Status</label>
                <select value={status} onChange={(e) => setStatus(e.target.value as IssueStatus)} style={{ width: "100%" }}>
                  {ALL_STATUSES.map((s) => (
                    <option key={s} value={s}>{STATUS_LABEL[s]}</option>
                  ))}
                </select>
              </div>

              <div>
                <label style={{ display: "block", marginBottom: 4, fontWeight: 500 }}>Priority</label>
                <select value={priority} onChange={(e) => setPriority(e.target.value as IssuePriority)} style={{ width: "100%" }}>
                  {ALL_PRIORITIES.map((p) => (
                    <option key={p} value={p}>{p.charAt(0) + p.slice(1).toLowerCase()}</option>
                  ))}
                </select>
              </div>

              <div>
                <label style={{ display: "block", marginBottom: 4, fontWeight: 500 }}>Assigned To</label>
                <select value={assignedToId} onChange={(e) => setAssignedToId(e.target.value)} style={{ width: "100%" }}>
                  <option value="">— Unassigned —</option>
                  {adminUsers?.map((u) => (
                    <option key={u.id} value={u.id}>{u.displayName ?? u.email}</option>
                  ))}
                </select>
              </div>
            </div>
          )}

          {submit.isError && (
            <p className="error-text">Failed to submit issue. Please try again.</p>
          )}

          <div style={{ display: "flex", gap: 8 }}>
            <button type="submit" className="primary" disabled={submit.isPending || attachments.anyUploading}>
              {submit.isPending ? "Submitting…" : "Submit Issue"}
            </button>
            <button type="button" onClick={() => navigate("/issues")}>
              Cancel
            </button>
          </div>
        </form>
      </div>
    </>
  );
}
